import axios from "axios";

import { API_PREFIX } from "config/env";
import {
  clearAuthSession,
  getRefreshToken,
  getUser,
  setAuthSession,
} from "services/storage";

const SESSION_EXPIRED_KEY = "crmSessionExpired";

const requestUrl = (input) => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input?.url || "";
};

const isLoginRequest = (url) => /\/auth\/login(?:[/?#]|$)/.test(String(url || ""));
const isRefreshRequest = (url) =>
  /\/auth\/refresh(?:[/?#]|$)/.test(String(url || ""));

const returnToLogin = () => {
  if (typeof window === "undefined" || window.location.pathname === "/login") {
    return;
  }

  clearAuthSession();

  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, "true");
  } catch {
    // Redirect still works when session storage is unavailable.
  }

  window.location.replace("/login");
};

export const consumeSessionExpiredFlag = () => {
  if (typeof window === "undefined") return false;

  try {
    const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === "true";
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return expired;
  } catch {
    return false;
  }
};

export const installAuthFailureGuard = () => {
  if (typeof window === "undefined" || window.__crmAuthFailureGuardInstalled) {
    return;
  }

  window.__crmAuthFailureGuardInstalled = true;

  const originalFetch = window.fetch.bind(window);
  let refreshPromise = null;
  const refreshAccessToken = async () => {
    if (refreshPromise) return refreshPromise;
    const refreshToken = getRefreshToken();
    if (!refreshToken) return "";

    refreshPromise = (async () => {
      const response = await originalFetch(`${API_PREFIX}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.accessToken) return "";
      const user = getUser();
      setAuthSession({
        token: data.accessToken,
        refreshToken: data.refreshToken || refreshToken,
        name: user?.name || user?.username,
        role: user?.role,
        user,
      });
      return data.accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  };

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url;
      const config = error?.config;
      if (
        status === 401 &&
        config &&
        !config.__sessionRetry &&
        !isLoginRequest(url) &&
        !isRefreshRequest(url) &&
        config.headers?.Authorization
      ) {
        const accessToken = await refreshAccessToken();
        if (accessToken) {
          config.__sessionRetry = true;
          config.headers.Authorization = `Bearer ${accessToken}`;
          return axios(config);
        }
      }
      if (status === 401 && !isLoginRequest(url) && !isRefreshRequest(url)) {
        returnToLogin();
      }
      return Promise.reject(error);
    },
  );

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const url = requestUrl(args[0]);
    const init = args[1] || {};
    const headers = new Headers(
      init.headers || (args[0] instanceof Request ? args[0].headers : {}),
    );
    if (
      response.status === 401 &&
      !init.__sessionRetry &&
      !isLoginRequest(url) &&
      !isRefreshRequest(url) &&
      headers.has("Authorization")
    ) {
      const accessToken = await refreshAccessToken();
      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
        return originalFetch(args[0], {
          ...init,
          headers,
          __sessionRetry: true,
        });
      }
    }
    if (
      response.status === 401 &&
      !isLoginRequest(url) &&
      !isRefreshRequest(url)
    ) {
      returnToLogin();
    }
    return response;
  };
};
